// frontend/src/components/PurchaseOrderEdit.js
import React, { useState, useEffect, useRef } from 'react';
import { Tabs, Descriptions, Typography, Divider, Select, Button, Table, InputNumber, Alert, Tooltip } from 'antd';
import { CheckOutlined, CloseOutlined, EditOutlined, WarningOutlined, InfoCircleOutlined } from '@ant-design/icons';

import { PO } from '../Constants';
import { toLocalDateTimeString } from '../utils/dateUtils';
import ServiceHistoryTab from './ServiceHistoryTab';
import { useConstants } from '../context/ConstantsContext';
import { updatePurchaseOrder, fetchPurchaseOrder as fetchPurchaseOrderData, fetchEquipment as fetchEquipmentData } from '../api';

const { TabPane } = Tabs;

const PurchaseOrderEdit = ({ poId, messageApi, onContractTypeChange }) => {
  const constants = useConstants();
  const [po, setPO] = useState(null);
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingContractType, setEditingContractType] = useState(false);
  const [contractType, setContractType] = useState(null);
  const [editingTravelTime, setEditingTravelTime] = useState(false);
  const [travelTimeAmount, setTravelTimeAmount] = useState(null);

  const editingRef = useRef(null);
  const travelTimeEditingRef = useRef(null);
  useEffect(() => {
    // Handle clicks outside the edit UI
    const handleClickOutside = (event) => {
      // Check if the click is inside an Ant Design dropdown
      const isAntDropdown = event.target.closest('.ant-select-dropdown');
      
      if (editingRef.current && !editingRef.current.contains(event.target) && !isAntDropdown) {
        setEditingContractType(false); // Cancel editing
        setContractType(po?.contract_type); // Reset to original value
      }
      if (travelTimeEditingRef.current && !travelTimeEditingRef.current.contains(event.target) && !isAntDropdown) {
        setEditingTravelTime(false); // Cancel editing
        setTravelTimeAmount(po?.travel_time_amount); // Reset to original value
      }
    };

    if (editingContractType === true || editingTravelTime === true) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editingContractType, editingTravelTime, po]);

  const fetchPurchaseOrder = async () => {
    try {
      setLoading(true);
      const data = await fetchPurchaseOrderData(poId);
      setPO(data);
      setContractType(data.contract_type);
      setTravelTimeAmount(data.travel_time_amount);
    } catch (error) {
      messageApi.error('Failed to load po details.');
    } finally {
      setLoading(false);
    }
  };

  const fetchEquipment = async () => {
    try {
      setLoading(true);
      const data = await fetchEquipmentData();
      const sortedData = data.sort((a, b) => a.name.localeCompare(b.name));
      setEquipment(sortedData);
    } catch (error) {
      messageApi.error('Failed to load available equipment.');
    } finally {
      setLoading(false);
    }
  };

  const handleContractTypeEdit = async (value) => {
    try {
      setEditingContractType(false);
      // Persist change to backend
      await updatePurchaseOrder(poId, { contract_type: value });

      // Refresh PO data from backend to ensure UI matches server state
      await fetchPurchaseOrder();

      // Update local contract type state immediately as well
      setContractType(value);

      // Notify parent list so the table row reflects the new contract type
      if (onContractTypeChange) {
        onContractTypeChange(poId, value);
      }

      messageApi.success('Contract type updated successfully!');
    } catch (error) {
      console.error('Error updating contract type:', error);
      messageApi.error('Failed to update contract type.');
      // Reset to original value on error
      setContractType(po?.contract_type);
    }
  };

  const handleTravelTimeEdit = async (value) => {
    try {
      setEditingTravelTime(false);
      const travelTimeValue = value === '' || value === null ? null : parseFloat(value);
      if (travelTimeValue !== null && (isNaN(travelTimeValue) || travelTimeValue < 0)) {
        messageApi.error('Travel time amount must be a positive number.');
        setTravelTimeAmount(po?.travel_time_amount);
        return;
      }
      await updatePurchaseOrder(poId, { travel_time_amount: travelTimeValue });
      setTravelTimeAmount(travelTimeValue);
      await fetchPurchaseOrder(); // Refresh to get updated total_amount_sum
      messageApi.success('Travel time amount updated successfully!');
    } catch (error) {
      messageApi.error('Failed to update travel time amount.');
      setTravelTimeAmount(po?.travel_time_amount);
    }
  };

  useEffect(() => {
    fetchPurchaseOrder();
    fetchEquipment();
  }, [poId]);

  if (loading || !po) {
    return <Typography>Loading...</Typography>;
  }

  // Aggregate PO summary using po_duration/po_quantity and subcontractor rates
  const calculatePoSummary = () => {
    if (!po || !po.service_histories) return [];

    // POs use actual duration without minimum enforcement
    const equipmentSummary = {};

    po.service_histories.forEach(sh => {
      sh.equipment.forEach(equip => {
        // Skip equipment that has been removed from PO (null PO fields)
        if (equip.po_duration !== null && equip.po_quantity !== null) {
          const key = equip.name;
          if (!equipmentSummary[key]) {
            equipmentSummary[key] = {
              name: equip.name,
              rawDuration: 0,
              effectiveDuration: 0,
              quantity: 0,
              rate: equip.subcontractor_rate_cad || equip.yeti_subcontractor_rate || 0,
              total: 0
            };
          }

          const rawDuration = equip.po_duration;
          // Use effective_duration from API (backend returns actual duration for POs, no minimum)
          const effectiveDuration = equip.effective_duration || rawDuration;
          const quantity = equip.po_quantity;
          const rate = equip.subcontractor_rate_cad || equip.yeti_subcontractor_rate || 0;

          equipmentSummary[key].rawDuration += rawDuration;
          equipmentSummary[key].effectiveDuration += effectiveDuration;
          equipmentSummary[key].quantity += quantity;
          // Use effective duration for total calculation (matches backend calculation)
          equipmentSummary[key].total += effectiveDuration * rate * quantity;
        }
      });
    });

    return Object.values(equipmentSummary);
  };

  const poSummaryColumns = [
    { title: 'Equipment', dataIndex: 'name', key: 'name' },
    {
      title: (
        <span>
          Total Duration (hrs){' '}
          <Tooltip title="Total actual duration across all service histories for this equipment">
            <InfoCircleOutlined style={{ fontSize: '12px', color: '#999' }} />
          </Tooltip>
        </span>
      ),
      key: 'duration',
      render: (_, record) => (
        <Tooltip 
          title={
            <div>
              <div style={{ marginBottom: '4px', fontWeight: 'bold' }}>Duration Breakdown:</div>
              {po?.service_histories?.map((sh, idx) => {
                const equip = sh.equipment?.find(e => e.name === record.name && e.po_duration !== null);
                if (!equip) return null;
                const rawDuration = equip.po_duration;
                const effectiveDuration = equip.effective_duration || rawDuration;
                return (
                  <div key={idx} style={{ fontSize: '11px', marginBottom: '2px' }}>
                    SH {idx + 1}: {rawDuration.toFixed(3)} hrs × {equip.po_quantity} = {effectiveDuration.toFixed(2)} hrs
                  </div>
                );
              })}
              <div style={{ marginTop: '4px', borderTop: '1px solid #555', paddingTop: '4px' }}>
                Total: {record.effectiveDuration.toFixed(2)} hrs
              </div>
            </div>
          }
        >
          <span style={{ cursor: 'help', borderBottom: '1px dashed #999' }}>
            {record.effectiveDuration.toFixed(2)}
          </span>
        </Tooltip>
      ),
    },
    {
      title: (
        <span>
          Total Quantity{' '}
          <Tooltip title="Total number of pieces across all service histories for this equipment">
            <InfoCircleOutlined style={{ fontSize: '12px', color: '#999' }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'quantity',
      key: 'quantity',
      render: (value, record) => (
        <Tooltip 
          title={
            <div>
              <div style={{ marginBottom: '4px', fontWeight: 'bold' }}>Quantity Breakdown:</div>
              {po?.service_histories?.map((sh, idx) => {
                const equip = sh.equipment?.find(e => e.name === record.name && e.po_quantity !== null);
                if (!equip) return null;
                return (
                  <div key={idx} style={{ fontSize: '11px', marginBottom: '2px' }}>
                    SH {idx + 1}: {equip.po_quantity} pieces
                  </div>
                );
              })}
              <div style={{ marginTop: '4px', borderTop: '1px solid #555', paddingTop: '4px' }}>
                Total: {value} pieces
              </div>
            </div>
          }
        >
          <span style={{ cursor: 'help', borderBottom: '1px dashed #999' }}>
            {value}
          </span>
        </Tooltip>
      ),
    },
    { title: 'Rate ($/hr)', dataIndex: 'rate', key: 'rate', render: (value) => `$${parseFloat(value).toFixed(2)}` },
    {
      title: (
        <span>
          Total ($){' '}
          <Tooltip title="Sum of (duration × rate × quantity) across all service histories for this equipment">
            <InfoCircleOutlined style={{ fontSize: '12px', color: '#999' }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'total',
      key: 'total',
      render: (value, record) => (
        <Tooltip 
          title={
            <div>
              <div style={{ marginBottom: '4px', fontWeight: 'bold' }}>Total Calculation:</div>
              {po?.service_histories?.map((sh, idx) => {
                const equip = sh.equipment?.find(e => e.name === record.name && e.po_duration !== null);
                if (!equip) return null;
                const effectiveDuration = equip.effective_duration || equip.po_duration;
                const lineTotal = effectiveDuration * record.rate * equip.po_quantity;
                return (
                  <div key={idx} style={{ fontSize: '11px', marginBottom: '2px' }}>
                    SH {idx + 1}: {effectiveDuration.toFixed(2)} hrs × ${record.rate.toFixed(2)}/hr × {equip.po_quantity} = ${lineTotal.toFixed(2)}
                  </div>
                );
              })}
              <div style={{ marginTop: '4px', borderTop: '1px solid #555', paddingTop: '4px' }}>
                Total: ${value.toFixed(2)}
              </div>
            </div>
          }
        >
          <span style={{ cursor: 'help', borderBottom: '1px dashed #999' }}>
            ${value.toFixed(2)}
          </span>
        </Tooltip>
      ),
    },
  ];

  const contractOptions =
  po.event_type === constants.event_type.snow
    ? [
        { label: constants.contract_type.accum, value: constants.contract_type.accum },
        { label: constants.contract_type.hourly, value: constants.contract_type.hourly },
        { label: constants.contract_type.per_push, value: constants.contract_type.per_push },
        { label: constants.contract_type.all_inclusive, value: constants.contract_type.all_inclusive },
      ]
    : [
        { label: constants.contract_type.per_visit, value: constants.contract_type.per_visit },
        { label: constants.contract_type.flat_rate, value: constants.contract_type.flat_rate },
        { label: constants.contract_type.all_inclusive, value: constants.contract_type.all_inclusive },
      ];

  return (
    <div>
      <Typography.Title level={3}>Edit Purchase Order</Typography.Title>
      <Divider />

      {/* PO Information */}
      <Descriptions title="Purchase Order Details" bordered column={1} labelStyle={{ width: '150px' }}>
        {po.event_type === constants.event_type.snow ? (
          <Descriptions.Item label="Site">{po.site_name}</Descriptions.Item>
        ) : (
          <Descriptions.Item label="Route">{po.route_name}</Descriptions.Item>
        )}
        <Descriptions.Item label="Subcontractor">{po.subcontractor_name}</Descriptions.Item>
        <Descriptions.Item label="Event">{po.event_name}</Descriptions.Item>
        <Descriptions.Item label="Contract Type">
          {editingContractType ? (
            <div ref={editingRef} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Select
                style={{ width: '200px' }}
                value={contractType}
                onChange={(value) => setContractType(value)}
                options={contractOptions}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Button
                  shape='circle'
                  icon={<CloseOutlined style={{ color: 'red' }} />}
                  onClick={() => {
                    setEditingContractType(false);
                    setContractType(po?.contract_type);
                  }}
                />
                <Button
                  shape='circle'
                  icon={<CheckOutlined style={{ color: 'green' }} />}
                  onClick={() => handleContractTypeEdit(contractType)}
                />
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography.Text>{contractType}</Typography.Text>
              <Button
                shape='circle'
                icon={<EditOutlined />}
                onClick={() => setEditingContractType(true)}
              />
            </div>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Total Duration">{po.total_duration} hours</Descriptions.Item>
        <Descriptions.Item label="Amount">${po.total_amount_sum.toFixed(2)}</Descriptions.Item>
        <Descriptions.Item label="Custom Amount">${po.custom_amount?.toFixed(2) || 'N/A'}</Descriptions.Item>
        <Descriptions.Item label="Travel Time Amount">
          {editingTravelTime ? (
            <div ref={travelTimeEditingRef} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <InputNumber
                style={{ width: '250px' }}
                value={travelTimeAmount}
                onChange={(value) => setTravelTimeAmount(value)}
                min={0}
                precision={2}
                placeholder="Enter amount"
                prefix="$"
                addonAfter={po?.country === 'Canada' ? 'CAD' : 'USD'}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Button
                  shape='circle'
                  icon={<CloseOutlined style={{ color: 'red' }} />}
                  onClick={() => {
                    setEditingTravelTime(false);
                    setTravelTimeAmount(po?.travel_time_amount);
                  }}
                />
                <Button
                  shape='circle'
                  icon={<CheckOutlined style={{ color: 'green' }} />}
                  onClick={() => handleTravelTimeEdit(travelTimeAmount)}
                />
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography.Text>
                {travelTimeAmount 
                  ? `$${travelTimeAmount.toFixed(2)} ${po?.country === 'Canada' ? 'CAD' : 'USD'}`
                  : 'N/A'}
              </Typography.Text>
              <Button
                shape='circle'
                icon={<EditOutlined />}
                onClick={() => setEditingTravelTime(true)}
              />
            </div>
          )}
        </Descriptions.Item>
      </Descriptions>

      {po.synced_contract_type && po.contract_type !== po.synced_contract_type && (
        <>
          <Divider />
          <Alert
            message="Contract Type Mismatch"
            description={`This purchase order was last synced with contract type "${po.synced_contract_type}" but is currently set to "${po.contract_type}". Please resync the PO to update the amount calculation and PDF with the new contract type.`}
            type="warning"
            showIcon
            icon={<WarningOutlined />}
          />
        </>
      )}

      <Divider />
      {po.service_histories && po.service_histories.length > 0 ? (
        <>
          {/* PO Summary Table */}
          <Typography.Title level={4}>PO Summary</Typography.Title>
          <Table
            columns={poSummaryColumns}
            dataSource={calculatePoSummary()}
            pagination={false}
            size="small"
            summary={(pageData) => {
              const total = pageData.reduce((sum, item) => sum + item.total, 0);
              return (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}>
                    <Typography.Text strong>Total</Typography.Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1}></Table.Summary.Cell>
                  <Table.Summary.Cell index={2}></Table.Summary.Cell>
                  <Table.Summary.Cell index={3}></Table.Summary.Cell>
                  <Table.Summary.Cell index={4}>
                    <Typography.Text strong>${total.toFixed(2)}</Typography.Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              );
            }}
          />

          <Divider />

          {/* Service Histories */}
          <Typography.Title level={4}>Service Histories</Typography.Title>
          <Tabs>
            {po.service_histories.map((shr) => (
              <TabPane
                key={shr.id}
                tab={
                  po.event_type === constants.event_type.snow ? (
                    <div>
                      <span>{toLocalDateTimeString(shr.started_at)}</span>
                      <br/>
                      <span>{`— ${toLocalDateTimeString(shr.ended_at)}`}</span>
                    </div>
                  ) : (
                    <span>{shr.site_name}</span>
                  )
                }
              >
                <ServiceHistoryTab
                  shr={shr}
                  equipment={equipment}
                  messageApi={messageApi}
                  type={PO}
                  onEquipmentChange={() => {
                    fetchPurchaseOrder(); // Refresh PO data when equipment changes
                  }}
                />
              </TabPane>
            ))}
          </Tabs>
        </>
      ) : (
        <>
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
            <Typography.Text>No service histories associated with this purchase order.</Typography.Text>
          </div>
        </>
      )}
    </div>
  );
};

export default PurchaseOrderEdit;